import React, {useState, useEffect, useContext} from 'react'
import axios from 'axios'
import IconCheckmarkCircle from '../../../../../img/icon-checkmark-circle.svg';
import IconAlertCircle from '../../../../../img/icon-alert-circle.svg';
import LoadingPanel from './loadingPanel';

// import redux
import { useDispatch } from 'react-redux';
import { toggleIndex } from '../redux/addOnPopupToggles/addOnPopupTogglesSlice';

//import context
import { Context } from '../courseTemplates';

/**
 * Course add ons for a current course instance
 * Opted to use axios instead of RTK Query for this component because
 * api calls are not as frequent and the data is not as complex
 * and all the data is used within this component
 * @param {Object} currentCourse - Data for the current course instance
 */
function CurrentCourseAddOns({currentCourse}) {
    const WEEK_IN_SECONDS = 604800;
    const TEN_DAYS_IN_SECONDS = 864000;

    const dispatch = useDispatch();

    // get our global context variables
    const value = useContext(Context);
    const { cartUrl } = value;

    // if the printed manual is in the cart or not
    const [printedManualInCart, setPrintedManualInCart] = useState(false);
    // tracks the state of different course add ons, after initial loading
    // state each course add on is indexed and have their own state
    const [addOnStatus, setAddOnStatus] = useState('loading');
    // loading state for the printed manual
    const [printedManualLoader, setPrintedManualLoader] = useState(true);

    /**
     * Ajax call to add a printed manual to the course enrollment
     */
    const addPrintedManual = () => {
        // show the printed manual loader
        setPrintedManualLoader(true)
        // params for the post to get the future courses for this course
        // these need to be url encoded in a query string for the post to work
        const params = new URLSearchParams();
        params.append('action', 'add_course_add_on');
        params.append('price', currentCourse.printedManualPrice);
        params.append('name', currentCourse.printedManualName);
        params.append('eId', currentCourse.enrollmentRecordID);
        params.append('cId', currentCourse.CourseRecordID);
        params.append('date', currentCourse.dateAttended);

        // send the post call!
        axios.post(urls.ajax, params, {
            headers: {
                'content-type': 'application/x-www-form-urlencoded'
            }
        }).then(function (response) {
            // set printed manual state to in cart
            setPrintedManualInCart(true);
            // remove the printed manual loader
            setPrintedManualLoader(false)
        })
        .catch(function (error) {
            console.log(error);
        });
    }

    const addCourseAddOn = (courseAddOn) => {
        setAddOnStatus({...addOnStatus, [courseAddOn.ItemRecordID]: 'loading'});
        // params for the post to get the future courses for this course
        // these need to be url encoded in a query string for the post to work
        const params = new URLSearchParams();
        params.append('action', 'add_course_add_on');
        params.append('price', courseAddOn.ItemPrice);
        params.append('name', courseAddOn.ItemName);
        params.append('eId', currentCourse.enrollmentRecordID);
        params.append('cId', currentCourse.CourseRecordID);
        params.append('date', currentCourse.dateAttended);
        params.append('productId', courseAddOn.ItemRecordID);

        // send the post call!
        axios.post(urls.ajax, params, {
            headers: {
                'content-type': 'application/x-www-form-urlencoded'
            }
        }).then(function (response) {
            setAddOnStatus({...addOnStatus, [courseAddOn.ItemRecordID]: 'cart'});
        })
        .catch(function (error) {
            console.log(error);
        });
    }
    
    /**
     * Ajax call to grab cart contents and check for course add ons and printed manual
     */
    const checkCartForAddOns = () => {
        const params = new URLSearchParams();
        params.append('action', 'fetch_cart_contents');
        params.append('enrollmentId', currentCourse.enrollmentRecordID)
        // send the post call!
        axios.post(urls.ajax, params, {
            headers: {
                'content-type': 'application/x-www-form-urlencoded'
            }
        }).then(function (response) {
            // if printed manual is in the cart
            if(response.data.printedManualIsInCart){
                setPrintedManualInCart(true)
            // if there are course add ons
            } if(response.data.courseAddOns){
                setCartContents(response.data.courseAddOns)
            // else set add on status to false to remove loader
            } else {
                setAddOnStatus(false);
            }
            // remove printed manual loader
            setPrintedManualLoader(false);
        })
        .catch(function (error) {
            console.log(error);
        });
    }

    /**
     * Sets the addon state with the cart items
     * @param {Array} cartItems - Cart items
     */
    const setCartContents = (cartItems) => {
        let newAddOnStatus = {...addOnStatus};
        if(Array.isArray(cartItems)){
            cartItems.map((cartItem) => {
                newAddOnStatus = {...newAddOnStatus, [cartItem.itemId]: 'cart' }
            });
            setAddOnStatus(newAddOnStatus);
        }
    }

    const cartHasAddOns = () => {
        //if the printed manual is in the cart
        if(printedManualInCart){
            return true;
        }
        if(addOnStatus === 'loading'){
            return false;
        }
        if(Object.values(addOnStatus).includes('cart')){
            return true;
        }
        
        return false;
    }

    const openPrintedManualPopup = (srcElem) => {
        if (window.jQuery && window.jQuery.magnificPopup) {
            window.jQuery.magnificPopup.open({
                items: {
                    src: `#${srcElem}`,
                    type: 'inline'
                },
                modal: true,
                alignTop: false,
                mainClass: 'mfp-blurred-bg mfp-fade'
            });
        }
    }

    const closePrintedManualPopup = () => {
        if (window.jQuery && window.jQuery.magnificPopup) {
            window.jQuery.magnificPopup.close();
        }
    }

    const continueAddToCart = () => {

        // Close the popup BEFORE calling addPrintedManual to avoid React re-render
        // conflicts and add small delay to let Magnific Popup finish closing before state updates

        closePrintedManualPopup();

        setTimeout(() => {
            addPrintedManual();
        }, 50);
    }

     /**
     *  Get JSX for a given add on product depending on add on status.
     * @param {Object} addOnProduct - Add on product data
     */
     const getCourseAddOnTitle = (addOnProduct) => {
        // if the course add on is already purchased
        if ((Date.now() > (currentCourse.startDate - WEEK_IN_SECONDS) * 1000) && !addOnProduct.Purchased) {
            return(
                <></>
            )
        } else {
            return(
                <>
                    <button className="link" onClick={() => dispatch(toggleIndex(addOnProduct.ItemRecordID))} >{addOnProduct.ItemName} ${addOnProduct.ItemPrice}</button><br/>
                </>
            )
        }
    }

    /**
     *  Get JSX for a given add on product depending on add on status.
     * @param {Object} addOnProduct - Add on product data
     */
    const getCourseAddOnSection = (addOnProduct) => {
        // if the course add on is already purchased
        if(addOnProduct.Purchased){
            return(
                <span className="text-btn-arrow text-btn-arrow--nolink">Purchased<span className="link-icon"><IconCheckmarkCircle/></span></span>
            )
        // if the course start date is 10 days or less from now
        } else if (Date.now() >= (currentCourse.startDate - TEN_DAYS_IN_SECONDS) * 1000) {
            return(
                <span className="text-btn-arrow text-btn-arrow--nolink">Call to Inquire</span>
            );
        }
        // if the course add on is out of stock
        else if (!addOnProduct.inStock){
            return(
                <span className="text-btn-arrow text-btn-arrow--nolink">Out of Stock</span>
            )
        // if the course add on product is in the cart
        }else if (addOnStatus === 'loading' || (addOnStatus[addOnProduct.ItemRecordID] && addOnStatus[addOnProduct.ItemRecordID] === 'loading')){
            return(
                <LoadingPanel small={true}/>
            )
        // if the course add on product is in the cart
        } else if (addOnStatus[addOnProduct.ItemRecordID] && addOnStatus[addOnProduct.ItemRecordID] === 'cart'){
            return(
                <span className="text-btn-arrow text-btn-arrow--green">Added to Cart<span className="link-icon"><IconCheckmarkCircle/></span></span>
            )
        // if the course add on product can be added to cart
        } else if (Date.now() > (currentCourse.startDate - WEEK_IN_SECONDS) * 1000) {
            return(<></>);
        } else {
            return(
                <a className="text-btn-arrow" onClick={()=>addCourseAddOn(addOnProduct)}>Add to Cart<span className="link-arrow"><IconCheckmarkCircle/></span></a>
            )
        }
    }

    /**
     * Get JSX for the printed manual section
     */
    const getPrintedManualSection = () => {
        // if loading printed manual data
        if(printedManualLoader){
            return (
                <LoadingPanel small={true}/>
            )
        // if printed manual cutoff reached
        }else if(currentCourse.printedManualCutoffReached){
            return(
                <>
                    <a className="text-btn-arrow" onClick={()=>openPrintedManualPopup('printed-manual-popup')} >Contact Kois<span className="link-arrow"><IconAlertCircle/></span></a>
                    <div id="printed-manual-popup" className="popup popup--purple mfp-hide">
                        <div className="popup-headline">Note</div>
                        <div className="popup-content">To purchase a printed course manual, please contact the Kois Center Team - <a href="mailto:coursemanagers@koiscenter.com">coursemanagers@koiscenter.com</a> / <a href="tel:+12066215310"> (206) 621-5310 ext. 333</a></div>
                        <button id="printed-manual-popup-close" className="btn c-btn--black prn-dlg-close">OK</button>
                    </div>
                </>
            )
        // if the printed manual is already purchased
        }else if(currentCourse.purchasedPrintedManual){
            return(
                <span className="text-btn-arrow text-btn-arrow--nolink">Purchased<span className="link-icon"><IconCheckmarkCircle/></span></span>
            )
        // if printed manual is in the cart
        }else if(printedManualInCart){
            return(

                <span className="text-btn-arrow text-btn-arrow--green">Added to Cart<span className="link-icon"><IconCheckmarkCircle/></span></span>

            )
        // default to state of being able to add the printed manual to the cart
        // KOIS-7257: open pop-up with printed manual size info before adding to cart
        } else {
            return(
                <>
                    <a className="text-btn-arrow" onClick={()=>openPrintedManualPopup('printed-manual-info-popup')} >Add to Cart<span className="link-arrow"><IconCheckmarkCircle/></span></a>
                    <div id="printed-manual-info-popup" className="popup popup--purple mfp-hide">
                        <div className="popup-headline">Additional Info</div>
                        <div className="popup-content">
                            <p>Note that the printed course manuals are rather large and can take up significant space in your luggage. Please be mindful of the size when packing.</p>
                            <p>A 3-day course manual is approximately 4 lbs. / 1.8 kg. Its dimensions are 3in X 12in x 12in / 5cm x 30cm x 30cm.</p>
                            <p>A 5-day course manual is approximately 8 lbs. / 3.5 kg. Its dimensions are 5in x 12in x 12in / 8cm x 30cm x 30cm.</p>
                        </div>
                        <button id="printed-manual-info-popup-continue" className="btn c-btn--black">Add</button>
                        <button id="printed-manual-info-popup-close" className="btn c-btn--black prn-dlg-close" style={{marginLeft: '4px'}}>Cancel</button>
                    </div>
                </>
            )
        }
    }

    useEffect(()=>{
        // check the cart first
        checkCartForAddOns()

        // Set up jQuery delegated event handlers for magnificPopup buttons
        // This is needed because magnificPopup moves the DOM element outside React's control
        if (window.jQuery) {
            window.jQuery(document).on('click', '.prn-dlg-close', function(e) {
                e.preventDefault();
                closePrintedManualPopup();
            });
            
            window.jQuery(document).on('click', '#printed-manual-info-popup-continue', function(e) {
                e.preventDefault();
                continueAddToCart();
            });
        }

        // Cleanup function to remove the event handlers when component unmounts
        return () => {
            if (window.jQuery) {
                window.jQuery(document).off('click', '.prn-dlg-close');
                window.jQuery(document).off('click', '#printed-manual-info-popup-continue');
            }
        };
    },[])

    // Component level JSX return statement ******************************************************
    return (
        <>
            <div className="table-course-list__row__section rg-12">
                <div className="flex-row">
                    <div className="col-lg-6 col-md-11 col-sm-12 col-xs-12">
                        <h5>Available Course Extras</h5>
                        <div className="grid-container">
                            {/* if the course has a printed manual */}
                            {currentCourse.hasPrintedManual &&
                                <div>Printed Manual ${currentCourse.printedManualPrice}<br/>
                                    {getPrintedManualSection()}
                                </div>
                            }
                            {/* if there are course add ons */}
                            {currentCourse.enrollmentProductAddOns && currentCourse.enrollmentProductAddOns.length > 0  &&
                                currentCourse.enrollmentProductAddOns.map((addOnProduct)=>{
                                    return (
                                        <>
                                            <div>
                                                {getCourseAddOnTitle(addOnProduct)}
                                                {getCourseAddOnSection(addOnProduct)}
                                            </div>
                                        </>
                                    )
                                })
                            }
                        </div>
                    </div>
                    
                    <div className="col-lg-4 col-lg-offset-2 col-md-12 col-md-offset-0 col-sm-12 col-sm-offset-0 col-xs-12 col-xs-offset-0">
                        {cartHasAddOns() &&
                            <a className="btn c-btn--light extras-checkout-btn" href={cartUrl}>Proceed to Checkout</a>
                        }
                    </div>
                </div>
            </div>
        </>
    )
}
export default CurrentCourseAddOns;